import { useCallback, useEffect, useState } from "react";
import {
  fetchPointsRulesForTenant,
  putPointsRulesForTenant,
  RULE_KEY_LABELS,
  type PointsRuleDto,
  type PointsRuleMode,
} from "../../../../api/pointsRules";
import { reportError } from "../../../../api/errors";
import { useAdminData } from "../../AdminDataContext";

type EditRow = PointsRuleDto;

export default function PointsRulesConfig() {
  const { tenantLegacyId } = useAdminData();
  const [rules, setRules] = useState<EditRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [form, setForm] = useState<EditRow | null>(null);

  const load = useCallback(async () => {
    if (!tenantLegacyId) return;
    setLoading(true);
    try {
      const rows = await fetchPointsRulesForTenant(tenantLegacyId);
      setRules(rows);
    } catch (e) {
      reportError(e);
    } finally {
      setLoading(false);
    }
  }, [tenantLegacyId]);

  useEffect(() => {
    void load();
  }, [load]);

  const openEdit = (row: EditRow) => {
    setEditingKey(row.ruleKey);
    setForm({ ...row });
  };

  const closeEdit = () => {
    setEditingKey(null);
    setForm(null);
  };

  const handleSaveOne = () => {
    if (!form) return;
    const original = rules.find((r) => r.ruleKey === form.ruleKey);
    const next = rules.map((r) =>
      r.ruleKey === form.ruleKey
        ? {
            ...form,
            name: original?.name ?? r.name,
            pointsMode: original?.pointsMode ?? r.pointsMode,
          }
        : r,
    );
    setRules(next);
    closeEdit();
  };

  const persistAll = async () => {
    if (!tenantLegacyId) return;
    setSaving(true);
    try {
      const fresh = await putPointsRulesForTenant(tenantLegacyId, rules);
      setRules(fresh);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    } catch (e) {
      reportError(e);
    } finally {
      setSaving(false);
    }
  };

  const modeLabel = (mode: PointsRuleMode) => (mode === "range" ? "积分范围" : "固定积分");

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h3 className="text-base font-bold text-gray-800">积分规则设置</h3>
          <p className="text-xs text-gray-400 mt-1">
            配置会员获取积分的固定规则。规则键名固定，每日签到与邀请好友等场景将读取此处配置。
          </p>
        </div>
        <button
          type="button"
          onClick={() => void persistAll()}
          disabled={saving || loading}
          className="px-4 py-2 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-60 cursor-pointer whitespace-nowrap"
        >
          {saving ? "保存中…" : savedFlash ? "已保存 ✓" : "保存全部"}
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-gray-400 py-8 text-center">加载中…</p>
      ) : (
        <div className="space-y-3">
          {rules.map((row) => {
            const fixedName = RULE_KEY_LABELS[row.ruleKey] ?? row.ruleKey;
            const ptsDisplay =
              row.pointsMode === "range"
                ? `+${Math.min(row.pointsMin, row.pointsMax)}~${Math.max(row.pointsMin, row.pointsMax)}`
                : `+${row.pointsFixed}`;
            return (
              <div
                key={row.ruleKey}
                className="bg-white border border-gray-100 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center gap-4"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-gray-900">{row.name || fixedName}</p>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-mono">
                      {row.ruleKey}
                    </span>
                    {!row.isEnabled ? (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">已停用</span>
                    ) : null}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {modeLabel(row.pointsMode)} · {ptsDisplay}
                    {row.description ? ` · ${row.description}` : ""}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => openEdit(row)}
                  className="px-3 py-2 rounded-xl border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
                >
                  编辑
                </button>
              </div>
            );
          })}
        </div>
      )}

      {editingKey && form ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h4 className="text-base font-bold text-gray-900 mb-4">
              编辑：{RULE_KEY_LABELS[form.ruleKey] ?? form.ruleKey}
            </h4>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">规则名称</label>
                <div className="w-full border border-gray-100 bg-gray-50 rounded-xl px-3 py-2 text-sm text-gray-700">
                  {RULE_KEY_LABELS[form.ruleKey] ?? form.name}
                </div>
                <p className="text-[11px] text-gray-400 mt-1">固定规则名称不可修改</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">积分类型</label>
                <div className="w-full border border-gray-100 bg-gray-50 rounded-xl px-3 py-2 text-sm text-gray-700">
                  {modeLabel(form.pointsMode)}
                </div>
                <p className="text-[11px] text-gray-400 mt-1">固定规则类型不可修改</p>
              </div>
              {form.pointsMode === "fixed" ? (
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">固定积分值</label>
                  <input
                    type="number"
                    min={0}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm"
                    value={form.pointsFixed}
                    onChange={(e) =>
                      setForm({ ...form, pointsFixed: Math.max(0, Number(e.target.value) || 0) })
                    }
                  />
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">最小积分</label>
                    <input
                      type="number"
                      min={0}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm"
                      value={form.pointsMin}
                      onChange={(e) =>
                        setForm({ ...form, pointsMin: Math.max(0, Number(e.target.value) || 0) })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">最大积分</label>
                    <input
                      type="number"
                      min={0}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm"
                      value={form.pointsMax}
                      onChange={(e) =>
                        setForm({ ...form, pointsMax: Math.max(0, Number(e.target.value) || 0) })
                      }
                    />
                  </div>
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">积分说明</label>
                <textarea
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm min-h-[80px]"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.isEnabled}
                  onChange={(e) => setForm({ ...form, isEnabled: e.target.checked })}
                />
                启用此规则
              </label>
            </div>
            <div className="flex gap-2 mt-6">
              <button
                type="button"
                onClick={closeEdit}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-medium cursor-pointer"
              >
                取消
              </button>
              <button
                type="button"
                onClick={handleSaveOne}
                className="flex-1 py-2.5 rounded-xl bg-red-600 text-white text-sm font-semibold cursor-pointer"
              >
                确定
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
